import { Alert, Box, Button, Card, CardContent, CardHeader, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckIcon from '@mui/icons-material/Check';
import CategoryIcon from '@mui/icons-material/Category';
import ListAltIcon from '@mui/icons-material/ListAlt';
import FolderIcon from '@mui/icons-material/Folder';
import GavelIcon from '@mui/icons-material/Gavel';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { cocCategories, cocImpactedElements } from '../../../services/mockData';
import StatusBadge, { COC_STATUS_MAP } from '../../../components/shared/StatusBadge';
import SectionHeader from '../../../components/shared/SectionHeader';

const COCRequestDetail = ({ request, onClose }) => {

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const category = cocCategories.find(cat => cat.id === request.category);
  const impactedElementNames = request.impactedElements.map(id => {
    const element = cocImpactedElements.find(el => el.id === id);
    return element ? element.name : id;
  });

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle>COC Request Details</DialogTitle>
      <DialogContent dividers>
        <Alert severity={request.status === 'Approved' ? 'success' : request.status === 'Rejected' ? 'error' : 'info'} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <StatusBadge status={request.status} map={COC_STATUS_MAP} />
            <Box>
              <Typography variant="subtitle2">Request ID: COC-{request.id}</Typography>
              <Typography variant="caption" color="text.secondary">Submitted {formatDate(request.submittedDate)} by {request.submittedBy}</Typography>
            </Box>
          </Stack>
        </Alert>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title={<SectionHeader title="COC Category" icon={<CategoryIcon fontSize="small" />} sx={{ mb: 0 }} />} />
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" sx={{ mb: 1 }}><Typography component="span" color="text.secondary">Category:</Typography> <strong>{request.categoryName}</strong></Typography>
                {category && (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}><Typography component="span" color="text.secondary">Description:</Typography> {category.description}</Typography>
                    <Alert severity="info" sx={{ mt: 1 }}>{category.complianceNote}</Alert>
                  </>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" sx={{ mb: 1 }}><Typography component="span" color="text.secondary">Discovery Date:</Typography> <strong>{new Date(request.discoveryDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong></Typography>
                <Typography variant="body2"><Typography component="span" color="text.secondary">Loan Number:</Typography> <strong>{request.loanNumber}</strong></Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title={<SectionHeader title="Change Description" icon={<DescriptionIcon fontSize="small" />} sx={{ mb: 0 }} />} />
          <CardContent>
            <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: 'block' }}>What Changed?</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{request.description}</Typography>
            <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: 'block' }}>Why Was This Not Known at Initial Disclosure?</Typography>
            <Typography variant="body2" color="text.secondary">{request.reasonNotKnown}</Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title={<SectionHeader title="Impacted Loan Elements" icon={<ListAltIcon fontSize="small" />} sx={{ mb: 0 }} />} />
          <CardContent>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {impactedElementNames.map((name, idx) => <Chip key={idx} label={name} size="small" color="primary" variant="outlined" icon={<CheckIcon />} />)}
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title={<SectionHeader title="Supporting Documentation" icon={<FolderIcon fontSize="small" />} sx={{ mb: 0 }} />} />
          <CardContent>
            <Stack spacing={1}>
              {request.supportingDocuments.map(doc => (
                <Stack key={doc.id} direction="row" spacing={2} alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
                  <DescriptionIcon color="primary" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={700}>{doc.fileName}</Typography>
                    <Typography variant="caption" color="text.secondary">{doc.fileSize} • Uploaded {formatDate(doc.uploadDate)}</Typography>
                    {doc.label && <Chip label={doc.label} size="small" color="info" variant="outlined" sx={{ ml: 1 }} />}
                  </Box>
                  <Button size="small" variant="outlined" startIcon={<DownloadIcon />}>Download</Button>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title={<SectionHeader title="Broker Attestation" icon={<GavelIcon fontSize="small" />} sx={{ mb: 0 }} />} />
          <CardContent>
            <Alert severity="error">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Compliance Attestation</Typography>
              <Typography variant="body2" color="text.secondary">{request.attestationText}</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ mt: 1 }}>Attested by {request.submittedBy} on {formatDate(request.submittedDate)}</Typography>
            </Alert>
          </CardContent>
        </Card>

        {request.reviewedBy && (
          <Card variant="outlined">
            <CardHeader title={<SectionHeader title="Lender Review" icon={<RateReviewIcon fontSize="small" />} sx={{ mb: 0 }} />} />
            <CardContent>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 4 }}><Typography variant="caption" color="text.secondary">Reviewed By</Typography><Typography variant="body2" fontWeight={700}>{request.reviewedBy}</Typography></Grid>
                <Grid size={{ xs: 12, md: 4 }}><Typography variant="caption" color="text.secondary">Review Date</Typography><Typography variant="body2" fontWeight={700}>{formatDate(request.reviewedDate)}</Typography></Grid>
                <Grid size={{ xs: 12, md: 4 }}><Typography variant="caption" color="text.secondary">Status</Typography><StatusBadge status={request.status} map={COC_STATUS_MAP} /></Grid>
              </Grid>
              {request.reviewNotes && <Alert severity="info" sx={{ mb: 2 }}>{request.reviewNotes}</Alert>}
              {request.revisedDisclosureIssued && <Alert severity="success"><Typography variant="subtitle2">Revised Loan Estimate Issued</Typography><Typography variant="body2" color="text.secondary">Issued on {formatDate(request.revisedDisclosureDate)}</Typography></Alert>}
            </CardContent>
          </Card>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

export default COCRequestDetail;
